import { Osdk } from "@osdk/client";
import { User } from "@unruly-osdk/sdk";
import { FC, Ref, useState } from "react";
import dialogCss from "../../common-styling/Dialog.module.css";
import { useServices } from "../../services/Services";
import Dialog from "../Dialog/Dialog";
import { DialogHandle } from "../Dialog/DialogTypes";
import Explanation from "../Dialog/Explanation";
import RadioField from "../Dialog/RadioField";

export type ReportLength = "Concise" | "Detailed";

interface FieldData {
  includePotentialRisk: boolean;
  includeRecommendedActions: boolean;
  reportDetail: ReportLength;
  days: number;
  impactLevel: number;
  maximumThemesInPdb: number;
}

const MAX_DAYS = 2;

const ReportPreferencesDialog: FC<{
  ref: Ref<DialogHandle>;
  onClose: () => void;
}> = function ({ ref, onClose }) {
  const services = useServices();
  const { data: user } = services.User.useGetCurrent();

  const saveMutation = services.User.useSaveReportPreferences();
  async function savePreferences(data: FormData) {
    const user = (await services.User.getCurrentAsync()) as Osdk.Instance<User>; // we always have a user here since the page has loaded
    const includePotentialRisks =
      data.get("include-potential-risks") === "true";
    const includeActions = data.get("include-recommended-actions") === "true";
    const reportDetail = ("" + data.get("report-detail")!) as ReportLength;
    const days = +data.get("days")!;
    const impactLevel = +data.get("impact-level")!;
    const maximumThemesInPdb = +data.get("maximum-themes-in-pdb")!;
    await saveMutation.mutateAsync({
      user,
      includeActions,
      includePotentialRisks,
      minimumImpactThreshold: impactLevel,
      reportDays: days,
      reportLength: reportDetail,
      maximumThemesInPdb,
    });
  }
  function onError(error: unknown) {
    console.error(error);
    return "Something went wrong saving your preferences. If this persists, you can contact Unruly support through accounts@unrulycorp.com";
  }

  return (
    <Dialog
      title="Report Preferences"
      action="Save Preferences"
      onSubmit={savePreferences}
      onError={onError}
      onClose={onClose}
      ref={ref}
    >
      {user ? <ReportPreferencesDialogInner user={user} /> : "Loading..."}
    </Dialog>
  );
};

function toFieldData(user: Osdk.Instance<User>): FieldData {
  return {
    includePotentialRisk: user.reportIncludeRisksAndConsideration ?? true,
    includeRecommendedActions: user.reportIncludeActions ?? true,
    reportDetail: (user.reportSummaryLength as ReportLength) || "Detailed",
    days: user.reportDayBacktrack || MAX_DAYS,
    impactLevel: user.reportEventImpactThreshold || 1,
    maximumThemesInPdb: user.reportMaxThemesInPdb || 3,
  };
}

const ReportPreferencesDialogInner: FC<{
  user: Osdk.Instance<User>;
}> = function ({ user }) {
  const [currentFieldData, setCurrentFieldData] = useState<FieldData>(
    toFieldData(user),
  );

  function modifyCurrentPreferences<K extends keyof FieldData>(
    field: K,
  ): (value: FieldData[K]) => void {
    return (value) =>
      setCurrentFieldData({ ...currentFieldData, [field]: value });
  }
  return (
    <>
      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>
          Include Potential Risks
          <Explanation>
            Include risks posed to your company from the events covered in the
            report.
          </Explanation>
        </div>
        <RadioField
          name="include-potential-risks"
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
          selectedValue={currentFieldData.includePotentialRisk}
          setSelectedValue={modifyCurrentPreferences("includePotentialRisk")}
        />
      </div>

      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>
          Include Recommended Actions to take
          <Explanation>
            Recommend actions that you could take based on the report insights
            in alignment with your company and role.
          </Explanation>
        </div>
        <RadioField
          name="include-recommended-actions"
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
          selectedValue={currentFieldData.includeRecommendedActions}
          setSelectedValue={modifyCurrentPreferences(
            "includeRecommendedActions",
          )}
        />
      </div>

      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>
          Choose level of detail of the report
          <Explanation>
            A <em>concise</em> summary is brief and to the point while a{" "}
            <em>detailed</em> one is more descriptive and comprehensive.
          </Explanation>
        </div>
        <RadioField
          name="report-detail"
          options={[
            { label: "Concise", value: "Concise" as const },
            { label: "Detailed", value: "Detailed" as const },
          ]}
          selectedValue={currentFieldData.reportDetail}
          setSelectedValue={modifyCurrentPreferences("reportDetail")}
        />
      </div>

      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>
          How far back in days should past events be included?
          <Explanation>
            The number of past days of events to be included in the report. For
            the Personal Daily Brief you have a maximum lookback of 2 days.
          </Explanation>
        </div>
        <div className={dialogCss.field}>
          <input
            type="number"
            min="1"
            max={MAX_DAYS}
            required
            name="days"
            value={currentFieldData.days}
            onChange={(e) => modifyCurrentPreferences("days")(+e.target.value)}
          />
        </div>
      </div>

      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>
          Pick the minimum Impact Threshold for events to explore
          <Explanation>
            Impact levels gauge an event's influence on global politics, from
            localized effects (level 1) to transformative shifts in the
            international political order (level 4).
          </Explanation>
        </div>
        <div className={dialogCss.field}>
          <input
            type="number"
            min="1"
            max="4"
            required
            name="impact-level"
            value={currentFieldData.impactLevel}
            onChange={(e) =>
              modifyCurrentPreferences("impactLevel")(+e.target.value)
            }
          />
        </div>
      </div>

      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>
          Maximum Number of Themes In PDB
          <Explanation>
            Set the maximum number of themes to include in your Personal Daily
            Brief. This helps control the length and focus of your report.
          </Explanation>
        </div>
        <div className={dialogCss.field}>
          <input
            type="number"
            min="1"
            max="20"
            required
            name="maximum-themes-in-pdb"
            value={currentFieldData.maximumThemesInPdb}
            onChange={(e) =>
              modifyCurrentPreferences("maximumThemesInPdb")(+e.target.value)
            }
          />
        </div>
      </div>
    </>
  );
};

export default ReportPreferencesDialog;
